/*
  PDP v2 · shared cart logic (window.QOTTv2)

  Used by every v2 add-to-cart button: the buy box, the sticky mobile bar,
  the final CTA and the ritual cross-sell. One place for:
    - the active offer (set early by snippets/qv2-offer-init.liquid)
    - attaching the discount code (offer = code), carried over from v1
    - adding the free gift line (offer = gift)
    - telling the page the cart changed (event: qott:cart-updated)
    - opening the Rebuy Smart Cart, or falling back to /cart

  Configuration comes from the buy box, which prints
  <script type="application/json" data-qott-offer-config>.
*/
(() => {
    if (window.QOTTv2) return;

    const OFFERS = ['none', 'shipping', 'code', 'gift'];
    const CODE_KEY = 'qott_offer_code';

    const store = {
        get(key) { try { return window.sessionStorage.getItem(key) || ''; } catch (err) { return ''; } },
        set(key, value) { try { window.sessionStorage.setItem(key, value); } catch (err) {} },
    };

    const offer = () => {
        const value = document.documentElement.getAttribute('data-qott-offer');
        return OFFERS.includes(value) ? value : 'none';
    };

    let cachedConfig = null;
    const config = () => {
        if (cachedConfig) return cachedConfig;
        const node = document.querySelector('script[data-qott-offer-config]');
        try { cachedConfig = node ? JSON.parse(node.textContent) : {}; } catch (err) { cachedConfig = {}; }
        return cachedConfig;
    };

    const routes = () => {
        const c = config();
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        return {
            cart: (c.cartUrl || root + 'cart'),
            add: (c.cartAddUrl || root + 'cart/add') + '.js',
            update: (c.cartUpdateUrl || root + 'cart/update') + '.js',
        };
    };

    const getCart = async () => {
        const res = await fetch(routes().cart + '.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } });
        return res.json();
    };

    // The code to attach, or '' when none applies. Only the "code" offer
    // attaches one. With "Allow a code from the URL" on, ?code=XYZ overrides
    // the section's code and is remembered for the browser session.
    const resolveCode = () => {
        const c = config();
        if (c.allowUrlCode) {
            let urlCode = '';
            try { urlCode = (new URLSearchParams(window.location.search).get('code') || '').trim(); } catch (err) {}
            if (urlCode) store.set(CODE_KEY, urlCode);
        }
        if (offer() !== 'code') return '';
        const remembered = c.allowUrlCode ? store.get(CODE_KEY) : '';
        return (remembered || c.code || '').trim();
    };

    // /cart/update.js `discount` REPLACES every code on the cart, so read the
    // current codes first and merge. Resolves with the updated cart.
    const attachCode = async (code) => {
        const cart = await getCart();
        const existing = (cart.discount_codes || []).map(d => d && d.code).filter(Boolean);
        if (existing.some(c => c.toUpperCase() === code.toUpperCase())) return cart;

        const res = await fetch(routes().update, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ discount: existing.concat(code).join(',') }),
        });
        const updated = await res.json();
        if (!res.ok || updated.status) {
            throw new Error(updated.description || updated.message || 'Could not apply discount code');
        }
        const applied = (updated.discount_codes || []).find(d => d && d.code && d.code.toUpperCase() === code.toUpperCase());
        if (!applied || applied.applicable === false) {
            console.warn(`QOTTv2: discount code "${code}" was added but is not applicable to this cart`);
        }
        return updated;
    };

    // Adds the gift line when the offer is "gift", one of the items qualifies,
    // and the gift is not already in the cart. The $0 price comes from an
    // automatic Buy X Get Y discount in Shopify; this only adds the line.
    const withGift = async (items) => {
        const gift = config().gift || {};
        if (offer() !== 'gift' || !gift.variantId) return items;
        const qualifying = (gift.qualifies || []).map(String);
        const qualifies = items.some(i => qualifying.length === 0 || qualifying.includes(String(i.id)));
        if (!qualifies) return items;
        try {
            const cart = await getCart();
            if ((cart.items || []).some(li => String(li.variant_id) === String(gift.variantId))) return items;
        } catch (err) { /* add anyway */ }
        return items.concat({ id: Number(gift.variantId), quantity: 1, properties: { _qott_gift: 'true' } });
    };

    const openCart = async (refetch) => {
        const rebuy = window.Rebuy;
        if (rebuy && rebuy.SmartCart && typeof rebuy.SmartCart.show === 'function') {
            if (refetch && rebuy.Cart && typeof rebuy.Cart.fetchCart === 'function') {
                try { await rebuy.Cart.fetchCart(); } catch (err) {}
            }
            rebuy.SmartCart.show();
            return;
        }
        window.location.href = routes().cart;
    };

    /**
     * Add items, apply the active offer, announce the new cart, open the cart.
     * items: [{ id: variantId, quantity: 1 }]
     * Resolves with the cart JSON. Rejects if the add itself fails; a failed
     * code or gift never blocks the add.
     */
    const add = async (items, options = {}) => {
        const payload = { items: await withGift(items) };
        const res = await fetch(routes().add, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok || data.status) {
            throw new Error(data.description || data.message || 'Could not add to cart');
        }

        let cart = null;
        const code = resolveCode();
        if (code) {
            try { cart = await attachCode(code); } catch (err) { console.warn('QOTTv2: could not attach offer code', err); }
        }
        if (!cart) {
            try { cart = await getCart(); } catch (err) {}
        }
        if (cart) document.dispatchEvent(new CustomEvent('qott:cart-updated', { detail: { cart } }));
        if (options.openCart !== false) await openCart(Boolean(code) || offer() === 'gift');
        return cart;
    };

    const formatMoney = (cents, currency) => {
        const amount = (Number(cents) || 0) / 100;
        try {
            return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: currency || 'USD' }).format(amount);
        } catch (err) {
            return '$' + amount.toFixed(2);
        }
    };

    // Wires any v2 form marked [data-qv2-add-form]: a hidden input "id"
    // holds the variant, the submit button gets loading dots, and errors
    // show in [data-qv2-error] inside the form.
    const bindForm = (form) => {
        if (!form || form.dataset.qv2Bound) return;
        form.dataset.qv2Bound = 'true';
        form.addEventListener('submit', async (e) => {
            e.preventDefault();
            const button = form.querySelector('[type="submit"]');
            const errorEl = form.querySelector('[data-qv2-error]');
            if (button && button.classList.contains('is-loading')) return;
            const idInput = form.querySelector('[name="id"]');
            const qtyInput = form.querySelector('[name="quantity"]');
            if (!idInput || !idInput.value) return;
            if (errorEl) { errorEl.textContent = ''; errorEl.hidden = true; }
            if (button) button.classList.add('is-loading');
            try {
                await add([{ id: Number(idInput.value), quantity: Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1) }]);
            } catch (err) {
                console.error('QOTTv2: add to cart failed', err);
                const message = err.message || 'Something went wrong adding this to your cart.';
                if (errorEl) { errorEl.textContent = message; errorEl.hidden = false; } else { window.alert(message); }
            } finally {
                if (button) button.classList.remove('is-loading');
            }
        });
    };

    const bindAll = () => document.querySelectorAll('form[data-qv2-add-form]').forEach(bindForm);

    window.QOTTv2 = { offer, add, getCart, attachCode, resolveCode, formatMoney, bindForm, bindAll, config };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', bindAll);
    } else {
        bindAll();
    }
    // Theme editor: sections re-render without a page load.
    document.addEventListener('shopify:section:load', (e) => {
        cachedConfig = null;
        bindAll();
        // Theme editor: re-apply the buy box "Default offer" after a setting change,
        // in case the editor did not re-run the section's inline script.
        if (window.Shopify && window.Shopify.designMode) {
            const bb = e.target && e.target.querySelector('[data-qv2-default-offer]');
            if (bb && OFFERS.includes(bb.dataset.qv2DefaultOffer)) {
                document.documentElement.setAttribute('data-qott-offer', bb.dataset.qv2DefaultOffer);
                document.documentElement.setAttribute('data-qott-offer-prio', '2');
            }
        }
    });
})();
